import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Film, Sparkles, Search, Loader2, Lightbulb, Wand2, Star,
  ChevronLeft, Tv, Folder,
} from "lucide-react";
import {
  listStoryboards,
  createStoryboard,
  deleteStoryboard,
  analyzeChannelForIdeas,
  generateChannelIdeas,
  createStoryboardFromIdea,
  automateStoryboardFromIdea,
  listAutomationQueue,
  resumeOrphanedAutomateRuns,
  cancelAutomationJob,
  saveStoryIdea,
  listActiveChannels,
  listChannelCreations,
  deleteActiveChannel,
  deleteChannelCreation,
} from "@/lib/api";
import CharacterProfileEditor from "@/components/CharacterProfileEditor";
import AutomationProgressBanner from "@/components/AutomationProgressBanner";
import ChannelSettingsPanel from "@/components/ChannelSettingsPanel";
import ReferenceVideoAnalyzer from "@/components/ReferenceVideoAnalyzer";
import { FactorySection, ConveyorDivider } from "@/components/FactoryUI";

// Normalise a YouTube handle so "@Bella-Play-Club", "bella-play-club",
// and "@bella-play-club" all count as the same channel key. Lowercase,
// strip leading @, trim whitespace. Empty inputs return "".
function normalizeHandle(h) {
  const s = (h || "").toString().trim().toLowerCase();
  return s.startsWith("@") ? s.slice(1) : s;
}

// Sentinel key used for storyboards that have no channel_handle
// attached (legacy items created before the channel-gate flow).
// URL uses `?channel=__unassigned__` to keep it explicit.
const UNASSIGNED_KEY = "__unassigned__";

/**
 * Storyboards — list of every video-storyboard the user has authored.
 * Top panel: Channel-driven story idea generator. Enter a @handle,
 * GPT-5.2 reviews the channel's recent uploads to learn characters
 * and tone, then generates 10 episode ideas. Pick one → it auto-
 * creates a storyboard pre-populated with scene descriptions.
 */
export default function StoryboardList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the source of truth for the picked channel so browser
  // back / refresh / deep-link all restore the same view. Empty
  // string = no channel picked yet (show the gate).
  const rawParam = (searchParams.get("channel") || "").trim();
  const selectedKey = rawParam === UNASSIGNED_KEY
    ? UNASSIGNED_KEY
    : normalizeHandle(rawParam);
  const hasSelection = selectedKey !== "";

  const [boards, setBoards] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStoryboards();
      setBoards(res?.storyboards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load storyboards");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    try {
      // Merge two sources of "channels":
      //   1. Phase 3 Active Channels (tracked live channels) — the
      //      historical source of Storyboards buckets.
      //   2. Phase 2 Channel Creations (kit builds from
      //      /create-channel) — so a kit the user just built appears
      //      here immediately as a valid bucket, even before it's
      //      launched to YouTube. De-dupe by normalised handle so a
      //      channel that's BOTH a kit AND a live channel doesn't
      //      double up.
      const [active, kits] = await Promise.all([
        listActiveChannels().catch(() => []),
        listChannelCreations().catch(() => ({ items: [] })),
      ]);
      const merged = [];
      const seen = new Map();  // normalized handle → merged entry (so we
      // can attach BOTH active_channel_id AND kit_job_id when a channel
      // exists in both sources — needed for cascade-delete on the gate).
      for (const ch of active || []) {
        const k = normalizeHandle(ch?.handle || "");
        if (!k) continue;
        if (!seen.has(k)) {
          const entry = { ...ch, source: "active", active_channel_id: ch?.channel_id || "" };
          seen.set(k, entry);
          merged.push(entry);
        } else {
          seen.get(k).active_channel_id = ch?.channel_id || "";
        }
      }
      const kitItems = kits?.items || [];
      for (const k of kitItems) {
        if (k?.status !== "complete") continue;
        const norm = normalizeHandle(k?.handle || "");
        if (!norm) continue;
        if (!seen.has(norm)) {
          const entry = {
            handle: k.handle,
            name: k.name,
            thumbnail: (k.images || {})["avatar.png"] || "",
            source: "kit",
            kit_job_id: k?.job_id || "",
          };
          seen.set(norm, entry);
          merged.push(entry);
        } else {
          const existing = seen.get(norm);
          existing.kit_job_id = k?.job_id || "";
          // Prefer a kit thumbnail if the active channel has none.
          if (!existing.thumbnail) {
            existing.thumbnail = (k.images || {})["avatar.png"] || "";
          }
        }
      }
      setChannels(merged);
    } catch (e) {
      // Non-fatal — user can still see the "unassigned" bucket +
      // channels derived from storyboard.channel_handle fallbacks.
      console.warn("Couldn't load channels", e);
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadChannels(); }, [load, loadChannels]);

  // Group storyboards by normalised channel_handle. Every board
  // lands in a bucket — those without a handle land in UNASSIGNED_KEY.
  const boardsByChannel = useMemo(() => {
    const map = new Map();
    for (const b of boards) {
      const k = normalizeHandle(b?.channel_handle || "") || UNASSIGNED_KEY;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(b);
    }
    return map;
  }, [boards]);

  // Merged "channel bucket" list for the gate view. Combines the
  // user's tracked channels (from Phase 3 · My Channels) with any
  // channel handles that appear on storyboards but AREN'T tracked
  // (eg imported/legacy work). Ensures nothing gets hidden.
  const channelBuckets = useMemo(() => {
    const buckets = new Map();
    // 1. Tracked channels always show up, even if they have 0 boards.
    for (const ch of channels) {
      const k = normalizeHandle(ch?.handle || "");
      if (!k) continue;
      buckets.set(k, {
        key: k,
        name: ch?.name || `@${k}`,
        handle: ch?.handle ? `@${normalizeHandle(ch.handle)}` : "",
        thumbnail: ch?.thumbnail || "",
        count: 0,
        source: "tracked",
        active_channel_id: ch?.active_channel_id || "",
        kit_job_id: ch?.kit_job_id || "",
      });
    }
    // 2. Merge in counts + orphan handles from storyboards.
    for (const [k, list] of boardsByChannel.entries()) {
      if (k === UNASSIGNED_KEY) continue;
      if (buckets.has(k)) {
        buckets.get(k).count = list.length;
      } else {
        // Orphan handle (has boards but not in "My Channels" yet).
        buckets.set(k, {
          key: k,
          name: `@${k}`,
          handle: `@${k}`,
          thumbnail: "",
          count: list.length,
          source: "orphan",
        });
      }
    }
    const arr = Array.from(buckets.values());
    // Sort: buckets with content first, then alphabetically.
    arr.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [channels, boardsByChannel]);

  const unassignedCount = (boardsByChannel.get(UNASSIGNED_KEY) || []).length;

  // Storyboards actually shown once a channel is picked.
  const filteredBoards = useMemo(() => {
    if (!hasSelection) return [];
    return boardsByChannel.get(selectedKey) || [];
  }, [hasSelection, selectedKey, boardsByChannel]);

  // Human-friendly channel label + handle for the picked bucket,
  // used in the header + create-form when scoped to a channel.
  const selectedMeta = useMemo(() => {
    if (!hasSelection) return null;
    if (selectedKey === UNASSIGNED_KEY) {
      return { label: "Unassigned storyboards", handle: "" };
    }
    const b = channelBuckets.find((x) => x.key === selectedKey);
    if (b) return { label: b.name, handle: b.handle, thumbnail: b.thumbnail };
    // Fallback for when channels haven't loaded yet.
    return { label: `@${selectedKey}`, handle: `@${selectedKey}` };
  }, [hasSelection, selectedKey, channelBuckets]);

  const pickChannel = useCallback((key) => {
    // Preserve any other query params (rare, but future-proof).
    const next = new URLSearchParams(searchParams);
    next.set("channel", key);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const clearChannel = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("channel");
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  // Track which channel key is currently being deleted so the tile
  // can show a "removing…" state and prevent double-click.
  const [deletingKey, setDeletingKey] = useState("");

  // Remove a channel bucket from the gate. This cascades to every
  // source the channel came from: the Phase 3 Active Channel
  // registration AND the Phase 2 Saved Kit. Storyboards themselves
  // stay put — they'll simply drop into the "Unassigned" bucket
  // (or the orphan handle bucket) so the user never loses work.
  const deleteChannelBucket = useCallback(async (bucket) => {
    if (!bucket) return;
    const boardCount = boardsByChannel.get(bucket.key)?.length || 0;
    const bits = [];
    if (bucket.active_channel_id) bits.push("• Stop tracking it on the Dashboard (Phase 3)");
    if (bucket.kit_job_id) bits.push("• Delete its Saved Channel Kit (Phase 2)");
    if (boardCount > 0) {
      bits.push(`• Move its ${boardCount} storyboard${boardCount === 1 ? "" : "s"} to the Unassigned bucket (not deleted)`);
    }
    if (bits.length === 0) {
      bits.push("• Remove it from this picker");
    }
    const msg =
      `Delete ${bucket.name} from your channel list?\n\n` +
      `This will:\n${bits.join("\n")}\n\n` +
      `This can't be undone. Continue?`;
    if (!window.confirm(msg)) return;
    setDeletingKey(bucket.key);
    try {
      const ops = [];
      if (bucket.active_channel_id) {
        ops.push(deleteActiveChannel(bucket.active_channel_id));
      }
      if (bucket.kit_job_id) {
        ops.push(deleteChannelCreation(bucket.kit_job_id));
      }
      // Fire in parallel — one may 404 (already gone); we don't want
      // that to block the other.
      const results = await Promise.allSettled(ops);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length && failed.length === results.length) {
        throw failed[0].reason;
      }
      toast.success(`Removed ${bucket.name}`);
      // If the currently-selected channel was the one we deleted,
      // bounce back to the gate so the user isn't stranded on a
      // ghost URL.
      if (selectedKey === bucket.key) clearChannel();
      await Promise.all([loadChannels(), load()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove channel");
    } finally {
      setDeletingKey("");
    }
  }, [boardsByChannel, selectedKey, clearChannel, loadChannels, load]);

  const onCreate = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) {
      toast.error("Enter a video name first");
      return;
    }
    setCreating(true);
    try {
      // Bind the storyboard to the currently-picked channel so
      // subsequent bumpers / thumbnail-refs / character-profiles all
      // resolve against the right channel without a second prompt.
      // Skip binding when creating inside the "Unassigned" bucket.
      const channelHandle =
        selectedKey && selectedKey !== UNASSIGNED_KEY
          ? `@${selectedKey}`
          : undefined;
      const sb = await createStoryboard({
        videoName: name,
        channelHandle,
      });
      setNewName("");
      toast.success(`Created "${sb.video_name}"`);
      navigate(`/storyboards/${sb.storyboard_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create storyboard");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (sb) => {
    if (!window.confirm(`Delete storyboard "${sb.video_name}"? This removes all scenes + previews.`)) return;
    try {
      await deleteStoryboard(sb.storyboard_id);
      toast.success("Deleted");
      setBoards((arr) => arr.filter((x) => x.storyboard_id !== sb.storyboard_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---------- Gate view (no channel picked) ----------
  if (!hasSelection) {
    return (
      <main
        data-testid="storyboard-list-page"
        className="px-6 py-8 max-w-5xl mx-auto space-y-8"
      >
        <header className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center min-w-[3rem] h-8 px-2 font-mono text-[10px] font-black uppercase tracking-[0.25em] bg-yellow-400 text-black rounded-sm no-pill">
              GATE
            </span>
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400">
              // CHANNEL SELECT · CONTENT FACTORY FLOOR
            </span>
          </div>
          <div className="h-px w-24 bg-gradient-to-r from-yellow-400/70 via-yellow-400/20 to-transparent" />
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight leading-none pt-2">
            Pick a channel to work on
          </h1>
          <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
            Storyboards are organised by channel. Choose which channel
            you&apos;re building for — every storyboard, character
            profile, bumper, and thumbnail reference stays scoped to
            that channel.
          </p>
        </header>

        <ConveyorDivider label="active production lines" />

        <section
          data-testid="storyboard-channel-gate"
          className="space-y-3"
        >
          {(channelsLoading || loading) && (
            <div className="font-mono text-xs text-zinc-500 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your channels…
            </div>
          )}

          {!channelsLoading && channelBuckets.length === 0 && unassignedCount === 0 && (
            <div
              data-testid="storyboard-channel-gate-empty"
              className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-sm text-zinc-500 space-y-3"
            >
              <div>
                You haven&apos;t added any channels yet. Add one from
                Phase 2 · Create Channel or Phase 3 · Dashboard first.
              </div>
              <Link
                to="/create-channel"
                data-testid="storyboard-channel-gate-create-channel"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-black px-4 py-2 rounded-sm transition-colors"
              >
                <Plus className="w-4 h-4" /> Create your first channel
              </Link>
            </div>
          )}

          {channelBuckets.length > 0 && (
            <ul
              data-testid="storyboard-channel-gate-list"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            >
              {channelBuckets.map((ch, chIdx) => (
                <li key={ch.key}>
                  <div
                    data-testid={`storyboard-channel-gate-tile-${ch.key}`}
                    className="relative w-full border border-zinc-800 hover:border-yellow-400/60 hover:bg-yellow-400/5 transition-colors rounded-sm bg-zinc-950/70 group"
                  >
                    {/* L-bracket corner marks — matches every other
                        Factory station card so the gate reads as part
                        of the production line rather than a plain grid. */}
                    <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-yellow-400/40 pointer-events-none" />
                    <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-yellow-400/40 pointer-events-none" />
                    <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-yellow-400/40 pointer-events-none" />
                    <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-yellow-400/40 pointer-events-none" />
                    <button
                      type="button"
                      data-testid={`storyboard-channel-gate-pick-${ch.key}`}
                      onClick={() => pickChannel(ch.key)}
                      disabled={deletingKey === ch.key}
                      className="w-full text-left p-4 pr-10 flex items-center gap-3 disabled:opacity-40"
                    >
                      {ch.thumbnail ? (
                        <img
                          src={ch.thumbnail}
                          alt=""
                          className="w-12 h-12 rounded-full border border-zinc-800 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full border border-zinc-800 bg-zinc-950 flex items-center justify-center flex-shrink-0">
                          <Tv className="w-5 h-5 text-zinc-600" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="inline-flex items-center justify-center min-w-[1.5rem] h-4 px-1 font-mono text-[8px] font-black uppercase tracking-[0.15em] bg-yellow-400/90 text-black rounded-sm no-pill">
                            {String(chIdx + 1).padStart(2, "0")}
                          </span>
                          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                            station
                          </span>
                        </div>
                        <div className="text-sm font-bold text-zinc-100 truncate">
                          {ch.name}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 truncate">
                          {ch.handle || "—"}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display text-2xl font-black text-yellow-400 leading-none tabular-nums">
                          {ch.count}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
                          storyboard{ch.count === 1 ? "" : "s"}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid={`storyboard-channel-gate-delete-${ch.key}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteChannelBucket(ch);
                      }}
                      disabled={deletingKey === ch.key}
                      title={`Remove ${ch.name} from your channel list`}
                      className="no-pill absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-sm text-zinc-600 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/40 transition-colors disabled:opacity-40 disabled:cursor-wait"
                    >
                      {deletingKey === ch.key ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </li>
              ))}

              {unassignedCount > 0 && (
                <li>
                  <button
                    type="button"
                    data-testid="storyboard-channel-gate-pick-unassigned"
                    onClick={() => pickChannel(UNASSIGNED_KEY)}
                    className="w-full text-left border border-dashed border-zinc-700 hover:border-zinc-400 hover:bg-zinc-900/40 transition-colors rounded-sm bg-black p-4 flex items-center gap-3"
                  >
                    <div className="w-12 h-12 rounded-full border border-zinc-800 bg-zinc-950 flex items-center justify-center flex-shrink-0">
                      <Folder className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-zinc-100 truncate">
                        Unassigned
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 truncate">
                        legacy · no channel bound
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display text-xl font-black text-zinc-300 leading-none">
                        {unassignedCount}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                        storyboard{unassignedCount === 1 ? "" : "s"}
                      </div>
                    </div>
                  </button>
                </li>
              )}
            </ul>
          )}
        </section>
      </main>
    );
  }

  // ---------- Channel-scoped view (channel picked) ----------
  return (
    <main
      data-testid="storyboard-list-page"
      className="px-6 py-8 max-w-5xl mx-auto space-y-8"
    >
      <header className="space-y-3">
        <button
          type="button"
          data-testid="storyboard-change-channel-btn"
          onClick={clearChannel}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-300 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Change channel
        </button>
        <div className="flex items-center gap-3">
          {selectedMeta?.thumbnail ? (
            <img
              src={selectedMeta.thumbnail}
              alt=""
              className="w-12 h-12 rounded-full border border-zinc-800 flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full border border-zinc-800 bg-zinc-950 flex items-center justify-center flex-shrink-0">
              {selectedKey === UNASSIGNED_KEY ? (
                <Folder className="w-5 h-5 text-zinc-500" />
              ) : (
                <Tv className="w-5 h-5 text-zinc-600" />
              )}
            </div>
          )}
          <div className="space-y-1 min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
              // STORYBOARDS
              {selectedMeta?.handle && (
                <span className="text-zinc-500"> · {selectedMeta.handle}</span>
              )}
            </div>
            <h1
              data-testid="storyboard-scope-heading"
              className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight truncate"
            >
              {selectedMeta?.label}
            </h1>
          </div>
        </div>
      </header>

      {selectedKey !== UNASSIGNED_KEY && (
        <FactorySection
          station="01"
          title="CHANNEL BLUEPRINT"
          description="Style, post format, narration voice, and default cast that every new storyboard on this channel inherits automatically."
          data-testid="factory-section-settings"
        >
          <ChannelSettingsPanel
            key={`settings-${selectedKey}`}
            handle={selectedKey}
            channelLabel={selectedMeta?.label}
          />
        </FactorySection>
      )}

      {selectedKey !== UNASSIGNED_KEY && <ConveyorDivider label="reference intake" />}

      {selectedKey !== UNASSIGNED_KEY && (
        <FactorySection
          station="02"
          title="REFERENCE INTAKE"
          description="Feed the factory. Any YouTube URL → visual style + transcript + 5 recast idea seeds ready to spawn."
          data-testid="factory-section-reference"
        >
          <ReferenceVideoAnalyzer
            key={`ref-${selectedKey}`}
            handle={selectedKey}
            channelLabel={selectedMeta?.label}
            onStoryboardSpawned={() => load()}
          />
        </FactorySection>
      )}

      {selectedKey !== UNASSIGNED_KEY && <ConveyorDivider label="idea engine" />}

      {selectedKey !== UNASSIGNED_KEY && (
        <FactorySection
          station="03"
          title="IDEA ENGINE"
          description="Auto-generate story ideas from your channel's own catalog. Every idea is pre-scoped to your named characters and audience."
          data-testid="factory-section-idea-engine"
        >
          <ChannelStoryGenerator
            key={selectedKey}
            initialHandle={selectedMeta?.handle || ""}
            onCreated={() => load()}
          />
        </FactorySection>
      )}

      <ConveyorDivider label="story assembly" />

      <FactorySection
        station="04"
        title="NEW STORYBOARD"
        description="Spin up a fresh storyboard for this channel. Blueprint defaults auto-apply."
        data-testid="factory-section-new-storyboard"
      >
        <form
          onSubmit={onCreate}
          data-testid="storyboard-new-form"
          className="border border-zinc-800 rounded-sm bg-zinc-950/50 p-5 flex flex-wrap items-end gap-3"
        >
        <label className="flex-1 min-w-[220px] space-y-1.5">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200">
            {selectedKey === UNASSIGNED_KEY
              ? "New blank storyboard (no channel)"
              : `New storyboard for ${selectedMeta?.handle || "this channel"}`}
          </span>
          <input
            data-testid="storyboard-new-name-input"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Bella Goes Camping"
            className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100"
          />
        </label>
        <button
          type="submit"
          data-testid="storyboard-new-submit"
          disabled={creating}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-black px-5 py-2.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" /> {creating ? "Creating…" : "Create"}
        </button>
      </form>
      </FactorySection>

      <ConveyorDivider label="production floor" />

      <FactorySection
        station="05"
        title="STORYBOARD SHELF"
        description="Every storyboard on this channel. Click any card to open the scene editor."
        data-testid="factory-section-storyboard-shelf"
      >
      <section data-testid="storyboard-list-section" className="space-y-2">
        {loading && (
          <div className="font-mono text-xs text-zinc-500">Loading…</div>
        )}
        {!loading && filteredBoards.length === 0 && (
          <div
            data-testid="storyboard-list-empty-for-channel"
            className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-sm text-zinc-500"
          >
            No storyboards yet for {selectedMeta?.label}. Use the form
            above to start one — or use the GPT-5.2 Idea Engine to
            generate a batch.
          </div>
        )}
        {!loading && filteredBoards.length > 0 && (
          <ul className="divide-y divide-zinc-900 border border-zinc-800 rounded-sm">
            {filteredBoards.map((b) => (
              <li
                key={b.storyboard_id}
                data-testid={`storyboard-row-${b.storyboard_id}`}
                className="flex flex-wrap items-center gap-3 p-4 hover:bg-zinc-950/60 transition-colors"
              >
                <Film className="w-4 h-4 text-yellow-400" />
                <Link
                  to={`/storyboards/${b.storyboard_id}`}
                  data-testid={`storyboard-open-${b.storyboard_id}`}
                  className="flex-1 min-w-[180px] text-zinc-100 hover:text-yellow-300 font-medium"
                >
                  {b.video_name}
                </Link>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  {b.scene_count} scene{b.scene_count === 1 ? "" : "s"}
                </span>
                <Link
                  to={`/storyboards/${b.storyboard_id}`}
                  data-testid={`storyboard-edit-${b.storyboard_id}`}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-200 border border-cyan-400/40 hover:border-cyan-300 px-2 py-1 rounded-sm inline-flex items-center gap-1"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </Link>
                <button
                  type="button"
                  data-testid={`storyboard-delete-${b.storyboard_id}`}
                  onClick={() => onDelete(b)}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400 hover:text-red-300 border border-red-500/40 hover:border-red-500/70 px-2 py-1 rounded-sm inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      </FactorySection>
    </main>
  );
}


function ChannelStoryGenerator({ onCreated, initialHandle = "" }) {
  const navigate = useNavigate();
  // When the parent binds this generator to a specific channel (via
  // the channel gate), always prefer that handle over whatever the
  // user last typed. localStorage is still used as a fallback so the
  // "Unassigned" bucket (which doesn't provide initialHandle) keeps
  // remembering the user's previous input.
  const [handle, setHandle] = useState(
    () => initialHandle || localStorage.getItem("ytm.idea.handle") || "",
  );
  // Inspiration channel: a larger, older sibling channel in the same
  // niche the creator wants to model after. We feed its top-viewed
  // recent videos + extracted title patterns into the idea generator
  // so the user's new ideas borrow from what's proven to work without
  // copying anyone verbatim. Persisted in localStorage on the
  // assumption that a single creator usually has ONE "north star"
  // channel they keep referencing — but cleanable on each run.
  const [inspirationHandle, setInspirationHandle] = useState(
    () => localStorage.getItem("ytm.idea.inspiration") || "",
  );
  // Optional theme/topic that constrains the idea batch to a single
  // subject (eg "camping", "first day of school", "saving money").
  // Trims down "blank-page" feel for users who already know what
  // they want to make. Persisted so the field survives refresh.
  const [ideaTheme, setIdeaTheme] = useState(
    () => localStorage.getItem("ytm.idea.theme") || "",
  );
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [ideasInspiration, setIdeasInspiration] = useState(null);
  // Echoes the theme back from the most recent generator call so the
  // results header can show a "// Theme: camping" chip — gives the
  // user an at-a-glance confirmation the constraint was applied.
  const [ideasThemeUsed, setIdeasThemeUsed] = useState(null);
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [excludedTitles, setExcludedTitles] = useState([]);
  const [usingIdeaTitle, setUsingIdeaTitle] = useState(null);
  // Set of normalized titles already saved to My Story Ideas, so we
  // can swap the ⭐ Save button to "Saved" without a round-trip on
  // every render. Updated on click + when the analyzed handle
  // changes.
  const [savedTitles, setSavedTitles] = useState(new Set());

  const onSaveIdea = useCallback(async (idea) => {
    const norm = (idea.title || "").trim().toLowerCase();
    if (!norm || savedTitles.has(norm)) return;
    if (!analysis?.channel?.handle) {
      toast.error("Analyze a channel first so the saved idea remembers its source.");
      return;
    }
    try {
      await saveStoryIdea(analysis.channel.handle, idea);
      toast.success(`Saved "${idea.title}" to My Story Ideas`);
      setSavedTitles((cur) => new Set([...cur, norm]));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save idea");
    }
  }, [analysis?.channel?.handle, savedTitles]);

  useEffect(() => {
    localStorage.setItem("ytm.idea.handle", handle);
  }, [handle]);

  useEffect(() => {
    localStorage.setItem("ytm.idea.inspiration", inspirationHandle);
  }, [inspirationHandle]);

  useEffect(() => {
    localStorage.setItem("ytm.idea.theme", ideaTheme);
  }, [ideaTheme]);

  // Seed `savedTitles` from the backend so already-saved ideas show
  // the muted "Saved" pill instead of letting the user double-save.
  // Refresh whenever the analyzed channel changes since saved ideas
  // are scoped per-channel-handle.
  useEffect(() => {
    if (!analysis?.channel?.handle) return;
    let cancelled = false;
    (async () => {
      try {
        const { listStoryIdeas } = await import("@/lib/api");
        const res = await listStoryIdeas();
        if (cancelled) return;
        const titlesForHandle = (res?.ideas || [])
          .filter((i) => i.handle === analysis.channel.handle)
          .map((i) => (i.title || "").trim().toLowerCase());
        setSavedTitles(new Set(titlesForHandle));
      } catch {
        // Best-effort — if this fails, user can still re-save.
      }
    })();
    return () => { cancelled = true; };
  }, [analysis?.channel?.handle]);

  const onAnalyze = async (e) => {
    e?.preventDefault?.();
    const h = handle.trim();
    if (!h) {
      toast.error("Enter a channel @handle first");
      return;
    }
    setAnalyzing(true);
    setIdeas([]);
    setExcludedTitles([]);
    try {
      const res = await analyzeChannelForIdeas(h);
      setAnalysis(res.analysis);
      toast.success(res.cached ? "Loaded cached analysis" : "Channel analyzed");
      // Fire-and-forget pre-warm of the inspiration channel so the
      // idea-engine call downstream is instant. Non-blocking — a
      // failure here doesn't prevent the user from continuing with
      // just their own channel.
      const insp = inspirationHandle.trim();
      if (insp && insp.replace(/^@/, "").toLowerCase() !== h.replace(/^@/, "").toLowerCase()) {
        analyzeChannelForIdeas(insp).then((r) => {
          if (r?.analysis) {
            toast.success(
              `Inspiration cached: ${r.analysis.channel?.name || insp}`,
              { duration: 4000 },
            );
          }
        }).catch((err) => {
          toast.warning(
            `Inspiration channel couldn't be analyzed: ${err?.response?.data?.detail || "unknown"}. Ideas will still generate using your channel only.`,
            { duration: 8000 },
          );
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't analyze channel");
    } finally {
      setAnalyzing(false);
    }
  };

  const onGenerateIdeas = async (more = false) => {
    if (!analysis) return;
    setGeneratingIdeas(true);
    try {
      const exclude = more ? excludedTitles : [];
      const res = await generateChannelIdeas(analysis.channel.handle, {
        excludeTitles: exclude, count: 10,
        inspirationHandle: inspirationHandle.trim(),
        theme: ideaTheme.trim(),
      });
      const newIdeas = res.ideas || [];
      if (more) {
        setIdeas((prev) => [...prev, ...newIdeas]);
      } else {
        setIdeas(newIdeas);
      }
      // Surface inspiration meta (channel name + extracted patterns)
      // so the user can see WHICH channel's hits seeded these ideas.
      setIdeasInspiration(res.inspiration || null);
      setIdeasThemeUsed(res.theme || null);
      setExcludedTitles((prev) => [
        ...prev,
        ...newIdeas.map((i) => i.title),
      ]);
      if (newIdeas.length === 0) {
        toast.info("No new ideas returned — try analyzing again");
      } else if (res.inspiration?.name) {
        toast.success(
          `${more ? "10 more" : "10"} ideas — inspired by ${res.inspiration.name}`,
        );
      } else {
        toast.success(`${more ? "10 more" : "10"} ideas generated`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate ideas");
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const onUseIdea = async (idea) => {
    if (usingIdeaTitle) return;
    setUsingIdeaTitle(idea.title);
    try {
      const res = await createStoryboardFromIdea(analysis.channel.handle, idea);
      toast.success(`Storyboard created — ${res.breakdown.scenes.length} scenes`);
      onCreated?.();
      navigate(`/storyboards/${res.storyboard_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create storyboard from idea");
    } finally {
      setUsingIdeaTitle(null);
    }
  };

  // Multiple Automate jobs may be in flight simultaneously — the
  // backend queues them so they run one-at-a-time (Seedance
  // rate-limit safety), but the UI shows the FULL list: running
  // + queued + recently-finished. We poll the queue endpoint
  // every 2s and re-render the stack.
  const [automationJobs, setAutomationJobs] = useState([]);
  const [dismissedJobIds, setDismissedJobIds] = useState(() => new Set());

  const refreshQueue = useCallback(async () => {
    try {
      const res = await listAutomationQueue();
      setAutomationJobs(res?.jobs || []);
    } catch {
      // Silent — transient errors shouldn't blow away the banner.
    }
  }, []);

  // Initial queue load + persistent 2s polling. We keep polling
  // even when no jobs are visible so a newly-started job (eg from
  // a different tab) shows up immediately.
  useEffect(() => {
    refreshQueue();
    const id = setInterval(refreshQueue, 2000);
    return () => clearInterval(id);
  }, [refreshQueue]);

  // Toast on transitions (running → completed/failed) without
  // re-firing on every poll. We track which job_ids we've already
  // toasted for in a ref.
  const toastedRef = useRef(new Set());
  useEffect(() => {
    for (const j of automationJobs) {
      if (j.status === "completed" && !toastedRef.current.has(`done:${j.job_id}`)) {
        toastedRef.current.add(`done:${j.job_id}`);
        toast.success(
          `"${j.video_name}" is ready — ${j.scenes_done}/${j.total_scenes} scenes built`,
        );
      } else if (j.status === "failed" && !toastedRef.current.has(`fail:${j.job_id}`)) {
        toastedRef.current.add(`fail:${j.job_id}`);
        toast.error(`Automation failed: ${j.error || "unknown error"}`);
      }
    }
  }, [automationJobs]);

  const onAutomateIdea = async (idea) => {
    setUsingIdeaTitle(idea.title);
    try {
      const res = await automateStoryboardFromIdea(
        analysis.channel.handle, idea,
      );
      const msg = res.status === "queued"
        ? `"${idea.title}" queued · position ${res.queue_position}`
        : `Automating "${idea.title}" — building ${res.scene_count} scenes`;
      toast.success(msg);
      // Push the user straight to the Review & Approve page so they
      // watch the build happen live. The banner on /review polls the
      // same automation_jobs queue and shows per-scene progress in
      // real-time, then the storyboard slides into the review list
      // below as soon as the job completes.
      navigate("/review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start automation");
    } finally {
      setUsingIdeaTitle(null);
    }
  };

  const visibleJobs = automationJobs.filter((j) => !dismissedJobIds.has(j.job_id));
  const runningCount = visibleJobs.filter((j) => j.status === "running").length;
  const queuedCount = visibleJobs.filter((j) => j.status === "queued").length;

  return (
    <section
      data-testid="channel-story-generator"
      className="border border-yellow-400/30 rounded-sm bg-gradient-to-br from-yellow-400/5 via-black to-black p-5 space-y-5"
    >
      {visibleJobs.length > 0 && (
        <div className="space-y-2" data-testid="channel-automation-queue">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
              // AUTOMATION QUEUE · {runningCount} running · {queuedCount} queued
            </div>
            {visibleJobs.filter((j) => j.status === "completed" || j.status === "failed").length > 0 && (
              <button
                type="button"
                data-testid="channel-automation-queue-dismiss-all"
                onClick={() => {
                  setDismissedJobIds((prev) => {
                    const next = new Set(prev);
                    for (const j of visibleJobs) {
                      if (j.status === "completed" || j.status === "failed") {
                        next.add(j.job_id);
                      }
                    }
                    return next;
                  });
                }}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-100"
              >
                dismiss finished
              </button>
            )}
          </div>
          {visibleJobs.map((job) => (
            <AutomationProgressBanner
              key={job.job_id}
              job={job}
              onDismiss={() => setDismissedJobIds((prev) => {
                const next = new Set(prev);
                next.add(job.job_id);
                return next;
              })}
              onOpenStoryboard={() => navigate(`/storyboards/${job.storyboard_id}`)}
              onCancel={async () => {
                if (!window.confirm(
                  `Cancel "${job.video_name || "this job"}"? Any partial renders already saved will stay on the storyboard.`,
                )) return;
                try {
                  await cancelAutomationJob(job.job_id);
                  toast.success("Job cancelled — worker will bail before the next scene");
                  refreshQueue();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't cancel job");
                }
              }}
              onResume={async () => {
                try {
                  const res = await resumeOrphanedAutomateRuns();
                  if (res?.resumed > 0) {
                    toast.success(`Re-launched ${res.resumed} stalled job${res.resumed > 1 ? "s" : ""} — activity should resume in a moment`);
                  } else {
                    toast.info("Nothing to resume — refreshing…");
                  }
                  refreshQueue();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't resume job");
                }
              }}
            />
          ))}
        </div>
      )}
      <div className="space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> // GPT-5.2 IDEA ENGINE
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-black text-white tracking-tight">
          Channel-driven story ideas
        </h2>
        <p className="text-xs text-zinc-400 max-w-xl leading-relaxed">
          Paste a YouTube @handle. GPT-5.2 reads the channel&apos;s recent
          uploads to learn its characters and tone, then proposes 10
          original episodes. Pick one — it auto-creates a fully scoped
          storyboard for you.
        </p>
      </div>

      <form
        onSubmit={onAnalyze}
        data-testid="channel-analyze-form"
        className="flex flex-wrap items-end gap-3"
      >
        <label className="flex-1 min-w-[220px] space-y-1.5">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200">
            Your channel handle
          </span>
          <input
            data-testid="channel-handle-input"
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="@bellasplayclub"
            className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100 font-mono"
          />
        </label>
        <label className="flex-1 min-w-[220px] space-y-1.5">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 flex items-center gap-2">
            Inspirational channel <span className="text-zinc-500 normal-case tracking-normal text-[10px]">(optional)</span>
          </span>
          <input
            data-testid="inspiration-handle-input"
            type="text"
            value={inspirationHandle}
            onChange={(e) => setInspirationHandle(e.target.value)}
            placeholder="@cocomelon"
            title="Paste a YouTube @handle of a creator you want to model story ideas after — their top videos will seed the idea generator."
            className="w-full bg-black border border-zinc-800 focus:border-emerald-400/60 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100 font-mono"
          />
        </label>
        <button
          type="submit"
          data-testid="channel-analyze-btn"
          disabled={analyzing || !handle.trim()}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-black px-5 py-2.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {analyzing ? "Analyzing…" : "Analyze Channel"}
        </button>
      </form>
      <p
        data-testid="inspiration-help-text"
        className="text-[11px] text-zinc-500 leading-relaxed max-w-2xl -mt-2"
      >
        Enter a channel you admire (e.g. <span className="text-emerald-300 font-mono">@cocomelon</span>,{" "}
        <span className="text-emerald-300 font-mono">@sherifflabrador</span>) — the idea engine
        analyzes their top-performing videos and blends those hit-patterns into your story ideas.
        Leave blank to use only your channel&apos;s own tone.
      </p>

      {analysis && (
        <AnalysisCard analysis={analysis} />
      )}

      {analysis && (
        <div
          data-testid="channel-ideas-relocated-callout"
          className="border-2 border-emerald-400/30 bg-emerald-400/5 rounded-sm px-4 py-3 flex flex-wrap items-center gap-3"
        >
          <Lightbulb className="w-4 h-4 text-emerald-300" />
          <div className="flex-1 min-w-[240px] font-mono text-[11px] text-zinc-300 normal-case tracking-normal leading-relaxed">
            The 10-ideas-from-your-channel generator moved to its own page so it can run analysis + inspiration in the background and stream ideas with theme support.
          </div>
          <Link
            to={`/generate-ideas?channel=${encodeURIComponent(analysis?.channel?.handle || handle || "")}`}
            data-testid="channel-go-to-generate-ideas-btn"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.25em] bg-emerald-400 text-black hover:bg-emerald-300 font-black px-4 py-2 rounded-sm transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" /> Generate Story Ideas →
          </Link>
        </div>
      )}
    </section>
  );
}


function AnalysisCard({ analysis }) {
  const { channel, summary } = analysis || {};
  const subs = channel?.subscribers;
  return (
    <div
      data-testid="channel-analysis-card"
      className="border border-zinc-800 bg-black/70 rounded-sm p-4 space-y-3"
    >
      <div className="flex items-center gap-3">
        {channel?.thumbnail && (
          <img
            src={channel.thumbnail}
            alt=""
            className="w-10 h-10 rounded-full border border-zinc-800"
          />
        )}
        <div className="space-y-0.5">
          <div className="text-sm font-bold text-zinc-100" data-testid="channel-analysis-name">
            {channel?.name}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {channel?.handle}
            {subs != null && (
              <>
                {" · "}{Intl.NumberFormat("en", { notation: "compact" }).format(subs)} subs
              </>
            )}
            {channel?.video_count != null && (
              <>{" · "}{channel.video_count} videos</>
            )}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <SummaryField label="Tone" value={summary?.tone} />
        <SummaryField label="Audience" value={summary?.target_audience} />
        <SummaryField label="Format" value={summary?.format} />
        <SummaryField label="Visual style" value={summary?.visual_style} />
      </div>

      {(summary?.characters || []).length > 0 && (
        <div className="space-y-1.5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Characters
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.characters.map((c, i) => (
              <div
                key={`${c.name}-${i}`}
                className="border border-zinc-800 bg-zinc-950 px-2 py-1 rounded-sm text-[11px] text-zinc-200"
                title={c.notes || ""}
              >
                <span className="font-bold">{c.name}</span>
                {c.role && <span className="text-zinc-500"> — {c.role}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(summary?.themes || []).length > 0 && (
        <div className="space-y-1.5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Themes
          </div>
          <div className="flex flex-wrap gap-1.5">
            {summary.themes.map((t, i) => (
              <span
                key={`${t}-${i}`}
                className="text-[10px] uppercase tracking-[0.15em] text-yellow-200 border border-yellow-400/30 px-1.5 py-0.5 rounded-sm font-mono"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      <CharacterProfileEditor
        handle={channel?.handle}
        initialVisuals={analysis?.visuals}
      />
    </div>
  );
}


function SummaryField({ label, value }) {
  if (!value) return null;
  return (
    <div className="space-y-0.5">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        {label}
      </div>
      <div className="text-zinc-200 leading-relaxed">{value}</div>
    </div>
  );
}


function IdeaCard({ idea, idx, busy, disabled, onUse, onAutomate, onSave, saved }) {
  const [whyOpen, setWhyOpen] = useState(false);
  const src = idea.inspiration_source;
  const hasWhy = !!(src && src.source_title);
  const fmtViews = (n) => {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return String(v);
  };
  return (
    <article
      data-testid={`channel-idea-card-${idx}`}
      className="border border-zinc-800 hover:border-emerald-400/50 transition-colors rounded-sm bg-zinc-950 p-3 space-y-2"
    >
      <h3
        data-testid={`channel-idea-title-${idx}`}
        className="font-display text-base font-black text-zinc-100 leading-tight"
      >
        {idea.title}
      </h3>
      {idea.hook && (
        <p className="text-xs text-emerald-200/90 italic leading-relaxed">
          {idea.hook}
        </p>
      )}
      {idea.premise && (
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          {idea.premise}
        </p>
      )}
      {/* Why this idea? — reveals the specific inspiration-channel
          hit + pattern this idea was modeled on. Lets the creator
          sanity-check the steering before they invest in a render. */}
      {hasWhy && (
        <div
          data-testid={`channel-idea-why-${idx}`}
          className="border-t border-zinc-800 pt-2"
        >
          <button
            type="button"
            data-testid={`channel-idea-why-toggle-${idx}`}
            onClick={() => setWhyOpen((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 transition-colors"
          >
            {whyOpen ? "[hide] why this idea?" : "why this idea? →"}
          </button>
          {whyOpen && (
            <div className="mt-1.5 space-y-1 text-[11px] leading-relaxed">
              <div className="text-zinc-300">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                  modeled on
                </span>{" "}
                <span className="text-cyan-200 font-bold">
                  &ldquo;{src.source_title}&rdquo;
                </span>{" "}
                <span className="text-zinc-500">
                  · {fmtViews(src.source_views)} views
                </span>
              </div>
              {src.pattern_used && (
                <div className="text-zinc-300">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                    pattern
                  </span>{" "}
                  <span className="text-zinc-100">{src.pattern_used}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid={`channel-idea-use-${idx}`}
          onClick={onUse}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] bg-emerald-400 text-black hover:bg-emerald-300 font-black px-3 py-1.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {busy ? "Building storyboard…" : "Use this idea"}
        </button>
        {onAutomate && (
          <button
            type="button"
            data-testid={`channel-idea-automate-${idx}`}
            onClick={onAutomate}
            disabled={disabled}
            title="Build the storyboard AND auto-render every scene's image + video preview"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-yellow-400 text-yellow-100 hover:text-black hover:bg-yellow-300 bg-yellow-400/15 font-black px-3 py-1.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            {busy ? "Starting…" : "Automate"}
          </button>
        )}
        {onSave && (
          <button
            type="button"
            data-testid={`channel-idea-save-${idx}`}
            onClick={onSave}
            disabled={disabled || saved}
            title={saved ? "Already saved to My Story Ideas" : "Bookmark to My Story Ideas — survives page refresh, Automate later"}
            className={[
              "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-2 px-3 py-1.5 rounded-sm transition-colors",
              saved
                ? "border-zinc-700 text-zinc-500 cursor-default"
                : "border-cyan-400 text-cyan-100 hover:text-black hover:bg-cyan-300 bg-cyan-400/10 hover:border-cyan-300",
            ].join(" ")}
          >
            <Star
              className={["w-3 h-3", saved ? "fill-zinc-500" : "fill-cyan-300"].join(" ")}
            />
            {saved ? "Saved" : "Save"}
          </button>
        )}
      </div>
    </article>
  );
}

